/**
 * ProfileLayerData — public contract for the JSON shape stored in
 * `InstallationProfileLayer.data`.
 *
 * A profile layer can describe any subset of these fields. The
 * `ProfileRenderer` composes one or more layers (base before chosen)
 * into a fully-formed `openclaw.json`, a workspace file map, and a
 * credentials map.
 *
 * NOTE: `bootstrap.soul` is INTENTIONALLY ABSENT — the SOUL.md file is
 * sacred and is set by the first chat, never by a layer.
 *
 * @since 1.0.0
 */
import { Schema } from "effect";

/**
 * OpenClaw SecretRef — first-party reference shape that points the
 * gateway at a value provided by a secrets provider rather than embedding
 * the secret in `openclaw.json`. Per OpenClaw docs, `source` selects the
 * resolution strategy and `provider` names the configured provider entry
 * (under `secrets.providers.<name>`); `id` is the per-secret identifier
 * the provider understands (an env var name for `env`, a relative path
 * for `file`, or an exec-provider lookup key for `exec`).
 *
 * @since 1.0.0
 */
export const SecretRef = Schema.Struct({
  source: Schema.Literal("env", "file", "exec"),
  provider: Schema.String,
  id: Schema.String,
});
export type SecretRef = Schema.Schema.Type<typeof SecretRef>;

/**
 * Configuration for an exec-based secrets provider, matching the OpenClaw
 * `secrets.providers.<name>` schema: `source` selects the resolution
 * strategy and `command` is the ABSOLUTE path to the executable the
 * gateway invokes with `args` VERBATIM — OpenClaw performs NO placeholder
 * substitution of any kind. The gateway spawns the command with an EMPTY
 * environment and cwd set to the command's own directory, writes
 * `{"protocolVersion":1,"provider":"<name>","ids":[...]}` to its stdin,
 * and expects `{"protocolVersion":1,"values":{"<id>":"<value>"},"errors":
 * {...}}` on stdout (single-id shortcut: a bare JSON string is accepted
 * as that id's value). The command file must be owned by the running
 * gateway user. Baseline command is the FM resolver clawd installs at
 * apply time (see `defaultSecretsProvider` in `ProfileRenderer.ts`).
 *
 * NOTE: the shape is `{ source, command, args }` — NOT the older
 * `{ type, binary, args }`. `command` must be absolute because the gateway
 * does not resolve it via `$PATH`.
 *
 * @since 1.0.0
 */
export const ExecSecretsProvider = Schema.Struct({
  source: Schema.Literal("exec"),
  command: Schema.String,
  args: Schema.Array(Schema.String),
});
export type ExecSecretsProvider = Schema.Schema.Type<typeof ExecSecretsProvider>;

/**
 * The FM-owned slice of `openclaw.json` emitted as a deep-merge PATCH
 * rather than a full file. The agent merges `patch` into the box's existing
 * `openclaw.json`; any dotted path listed in `replacePaths` is REPLACED
 * wholesale instead of deep-merged, so removals propagate idempotently on
 * re-publish (e.g. dropping a connector's `tools.exec.pathPrepend` dir).
 *
 * @since 1.0.0
 */
export const OpenclawConfigPatch = Schema.Struct({
  patch: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
  replacePaths: Schema.Array(Schema.String),
});
export type OpenclawConfigPatch = Schema.Schema.Type<typeof OpenclawConfigPatch>;

/**
 * A connection declaration within a profile layer.
 *
 * `secrets` lists the field names within `config` whose values must be
 * emitted as `SecretRef` objects in the rendered openclaw.json. Field
 * values that aren't named in `secrets` pass through unchanged.
 *
 * `id` is the stable connection identifier (typically the
 * `installation_connections.id`); when present it is used as the
 * SecretRef id prefix. Otherwise `${provider}::${accountKey}` is used.
 *
 * @since 1.0.0
 */
export const ProfileConnection = Schema.Struct({
  id: Schema.optional(Schema.String),
  provider: Schema.String,
  accountKey: Schema.String,
  /**
   * Human-facing account label from `installation_connections.accountLabel`
   * — for google this is the customer email, whereas `accountKey` is the
   * opaque provider subject id (Google's `sub`). The connector renderer
   * substitutes `{{accountEmail}}` from this field, falling back to
   * `accountKey` for providers that do not split the two.
   *
   * @since 1.0.0
   */
  accountLabel: Schema.optional(Schema.String),
  enabled: Schema.Boolean,
  config: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
  secrets: Schema.optional(Schema.Array(Schema.String)),
  /**
   * Token expiry in Unix seconds. Surfaced for OAuth connections so
   * the renderer can emit `authProfiles[].expires` from the
   * `installation_connections` row's `tokenExpiresAt`.
   *
   * @since 1.0.0
   */
  tokenExpiresAtSeconds: Schema.optional(Schema.Number),
});
export type ProfileConnection = Schema.Schema.Type<typeof ProfileConnection>;

/**
 * An LLM provider declaration within a profile layer.
 *
 * `secrets` lists the credential field names that must be emitted as
 * `SecretRef` objects under `model.providers[<provider>]` in the
 * rendered openclaw.json (e.g. `["apiKey"]`).
 *
 * @since 1.0.0
 */
export const ProfileLlmProvider = Schema.Struct({
  provider: Schema.Literal("openai", "anthropic"),
  accountKey: Schema.String,
  priority: Schema.Number,
  modelDefault: Schema.String,
  modelFallbacks: Schema.Array(Schema.String),
  secrets: Schema.optional(Schema.Array(Schema.String)),
});
export type ProfileLlmProvider = Schema.Schema.Type<typeof ProfileLlmProvider>;

/**
 * A skill declaration within a profile layer.
 *
 * `by-name` references a built-in skill by name; the renderer emits the
 * name in `agents.defaults.skills` but does not write any files.
 *
 * `by-value` carries inline skill content; the renderer emits files at
 * `skills/<name>/<path>`. The `content` map's keys are workspace-relative
 * file paths under the skill directory (e.g. `SKILL.md`, `scripts/foo.sh`).
 *
 * @since 1.0.0
 */
export const ProfileSkill = Schema.Struct({
  name: Schema.String,
  mode: Schema.Literal("by-name", "by-value"),
  content: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.String })),
});
export type ProfileSkill = Schema.Schema.Type<typeof ProfileSkill>;

/**
 * A managed skill-install declaration within a profile layer.
 *
 * Distinct from `skills`: `skills` carries by-name / by-value content the
 * renderer surfaces in `agents.defaults.skills` and (for by-value) writes
 * to disk. `skillsInstall` instead instructs the agent to invoke
 * `openclaw skills install <slug>` against the local skills binary on the
 * installation — the slug identifies a published skill the agent will
 * resolve and install idempotently.
 *
 * @since 1.0.0
 */
export const ProfileSkillInstall = Schema.Struct({
  slug: Schema.String,
});
export type ProfileSkillInstall = Schema.Schema.Type<typeof ProfileSkillInstall>;

/**
 * A private skill-archive assignment within a profile layer.
 *
 * Distinct from `skillsInstall`: `skillsInstall` slugs are resolved and
 * installed by the local `openclaw` CLI (`openclaw skills install <slug>`),
 * whereas a `skillArchives` entry references a control-plane-hosted zip
 * archive delivered onto the box via the OpenClaw gateway uploaded-archive
 * flow (`skills.upload.*` / `skills.install({source:"upload"})`). The
 * renderer emits only `{slug, version}` — the archive's `sha256` / `sizeBytes`
 * stay server-side and are supplied by the mgmt archive-download RPC.
 *
 * Composed last-wins by `slug` across layers (a chosen layer overrides the
 * base layer's `version` for the same slug).
 *
 * @since 1.0.0
 */
export const ProfileSkillArchive = Schema.Struct({
  slug: Schema.String,
  version: Schema.String,
});
export type ProfileSkillArchive = Schema.Schema.Type<typeof ProfileSkillArchive>;

/**
 * A cron job declaration within a profile layer.
 * @since 1.0.0
 */
export const ProfileCronJob = Schema.Struct({
  name: Schema.String,
  schedule: Schema.String,
  command: Schema.String,
  description: Schema.String,
});
export type ProfileCronJob = Schema.Schema.Type<typeof ProfileCronJob>;

/**
 * Heartbeat configuration within a profile layer.
 * @since 1.0.0
 */
export const ProfileHeartbeat = Schema.Struct({
  every: Schema.String,
  target: Schema.String,
});
export type ProfileHeartbeat = Schema.Schema.Type<typeof ProfileHeartbeat>;

/**
 * Bootstrap workspace files declared by a profile layer.
 *
 * SOUL.md is INTENTIONALLY ABSENT — sacred, set by the first chat.
 *
 * @since 1.0.0
 */
export const ProfileBootstrap = Schema.Struct({
  user: Schema.optional(Schema.String),
  tools: Schema.optional(Schema.String),
  agents: Schema.optional(Schema.String),
  identity: Schema.optional(Schema.String),
});
export type ProfileBootstrap = Schema.Schema.Type<typeof ProfileBootstrap>;

/**
 * A plugin declaration within a profile layer. `spec` is the OpenClaw
 * plugin specifier (the same string accepted by `openclaw plugins
 * install`); the renderer composes plugins last-wins by `spec` across
 * layers, and the agent shells out to `openclaw plugins install <spec>`
 * idempotently after the encrypted-secrets dance.
 *
 * @since 1.0.0
 */
export const ProfilePlugin = Schema.Struct({
  spec: Schema.String,
  /**
   * Plugin config the renderer emits under `plugins.entries.<id>.config`
   * for a BARE spec (`memory-core`, `active-memory` — an id, not an npm
   * spec / archive / URL). The base layer uses it to turn memory-core's
   * Dreaming off (`{ dreaming: { enabled: false } }`). Ignored for non-bare
   * specs, whose entries are projected by their own bundle (discord,
   * exchange-mail).
   *
   * @since 1.0.0
   */
  config: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
});
export type ProfilePlugin = Schema.Schema.Type<typeof ProfilePlugin>;

/**
 * Auth-profile entry rendered into `RenderedProfile.authProfiles`.
 *
 * Mirrors the OpenClaw `auth-profiles.json` row shape (`profileId`,
 * `mode`, `provider`, `accessToken`, `refreshToken`, `expires`) plus
 * the `copyToAgents` flag that instructs OC to fan the profile to
 * every agent under `${OPENCLAW_STATE_DIR}/agents/<id>/agent/`.
 *
 * Today only `mode: "oauth"` / `provider: "openai-codex"` are emitted —
 * the M2 Codex bridge. Other modes/providers will join as we extend
 * the bridge to additional connectors.
 *
 * @since 1.0.0
 */
export const AuthProfile = Schema.Struct({
  profileId: Schema.String,
  mode: Schema.Literal("oauth"),
  provider: Schema.Literal("openai-codex"),
  accessToken: Schema.String,
  refreshToken: Schema.String,
  expires: Schema.Number,
  copyToAgents: Schema.Literal(true),
});
export type AuthProfile = Schema.Schema.Type<typeof AuthProfile>;

/**
 * Literal substitution token the agent splices into rendered connector
 * file contents / command argv / command env values immediately before
 * writing the file or spawning the command. Plaintext fetched via
 * `mgmt.fetchConnectionSecret`; NEVER survives into stdout/stderr
 * captures or audit-log payloads.
 *
 * @since 1.0.0
 */
export const ACCESS_TOKEN_PLACEHOLDER = "{{accessToken}}";

/**
 * Counterpart to {@link ACCESS_TOKEN_PLACEHOLDER}.
 *
 * @since 1.0.0
 */
export const REFRESH_TOKEN_PLACEHOLDER = "{{refreshToken}}";

/**
 * A non-interactive CLI bootstrap step a layer declares that the agent
 * must run idempotently against the installation.
 *
 * Shape contract:
 * - `id` — human-readable identifier surfaced in audit-log messages.
 * - `command` — binary name. The agent invokes it via
 *   `ShellRunner.exec` so `PATH` resolution is the platform default.
 * - `args` — arguments passed to the binary. May contain
 *   {@link REFRESH_TOKEN_PLACEHOLDER} substrings that the agent
 *   resolves to plaintext credentials at exec time.
 * - `env` — optional environment overrides merged into the child
 *   process env at exec time.
 * - `runOnceKey` — idempotency key. The agent skips the step on
 *   subsequent applies once the key is recorded as completed.
 *
 * @since 1.0.0
 */
export const ProfileCliBootstrap = Schema.Struct({
  id: Schema.String,
  command: Schema.String,
  args: Schema.Array(Schema.String),
  env: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.String })),
  runOnceKey: Schema.String,
});
export type ProfileCliBootstrap = Schema.Schema.Type<typeof ProfileCliBootstrap>;

/**
 * Octal file-mode pattern accepted by {@link ProfileCredentialFile.mode}.
 * Must be exactly four characters: a leading `0` followed by three octal
 * digits (e.g. `"0600"`, `"0644"`, `"0700"`). The leading `0` makes the
 * value unambiguously octal at every consumer that re-parses the string.
 *
 * @since 1.0.0
 */
const CREDENTIAL_FILE_MODE_PATTERN = /^0[0-7]{3}$/;

/**
 * A declarative, on-disk credential file the M1 agent must write to the
 * installation. This is the primitive every higher-level credential
 * emitter (auth-profiles, sops blobs, gogcli credentials.json, …) lowers
 * into; downstream renderers and the agent consume a single, uniform
 * `credentialFiles` array.
 *
 * Shape contract:
 * - `path` — destination file path on the installation. May be absolute
 *   (`/etc/foo/bar`) or tilde-expanded (`~/.config/foo/bar`); the agent
 *   resolves `~` to the installation user's `$HOME` at apply time. The
 *   renderer does not normalise the value — it is passed through verbatim.
 * - `content` — the file body, written verbatim. Newlines and trailing
 *   whitespace are preserved exactly.
 * - `mode` — POSIX permission bits as a 4-character octal string
 *   (`"0600"`, `"0644"`, `"0700"`, …). Rejected at decode time if it does
 *   not match {@link CREDENTIAL_FILE_MODE_PATTERN}.
 *
 * @since 1.0.0
 */
export const ProfileCredentialFile = Schema.Struct({
  path: Schema.String,
  content: Schema.String,
  mode: Schema.String.pipe(Schema.pattern(CREDENTIAL_FILE_MODE_PATTERN)),
});
export type ProfileCredentialFile = Schema.Schema.Type<typeof ProfileCredentialFile>;

/**
 * The full shape of a profile layer's `data` JSONB column.
 *
 * Every field is optional. A layer may describe any subset, and the
 * renderer composes layers using rules in `compose.ts`.
 *
 * @since 1.0.0
 */
export const ProfileLayerData = Schema.Struct({
  /**
   * Wizard-authored connection list. Post-cutover the renderer
   * **ignores** this field and reads connections from
   * `installation_connections` instead; we keep the property in the
   * schema so the customer onboarding wizard can keep its in-progress
   * UI state until the follow-up issue replaces it.
   *
   * @deprecated Reads at render time go through `installation_connections`.
   */
  connections: Schema.optional(Schema.Array(ProfileConnection)),
  llmProviders: Schema.optional(Schema.Array(ProfileLlmProvider)),
  skills: Schema.optional(Schema.Array(ProfileSkill)),
  /**
   * Managed `openclaw skills install <slug>` invocations the agent runs
   * after the apply step. Composed last-wins by `slug` across layers.
   * Distinct from `skills` (which is by-name / by-value content).
   *
   * @since 1.0.0
   */
  skillsInstall: Schema.optional(Schema.Array(ProfileSkillInstall)),
  /**
   * Private skill archives assigned to this installation. Each entry is a
   * control-plane-hosted zip archive installed on the box via the OpenClaw
   * gateway uploaded-archive flow (as opposed to `skillsInstall` slugs the
   * local `openclaw` CLI resolves). Composed last-wins by `slug` across
   * layers. When at least one archive is assigned the renderer folds
   * `skills.install.allowUploadedArchives: true` into the emitted config
   * patch — the gateway refuses the upload flow without that flag.
   *
   * @since 1.0.0
   */
  skillArchives: Schema.optional(Schema.Array(ProfileSkillArchive)),
  /**
   * Layer-declared cron jobs. The renderer **ignores** this field: the pinned
   * OpenClaw models the top-level `cron` config key as a settings-only object
   * (`additionalProperties: false`) and stores cron JOBS in the gateway's cron
   * store (provisioned via `openclaw cron add`), not in `openclaw.json`.
   * Emitting a job-name-keyed map here is rejected by the binary and fails the
   * whole config patch on a fresh box (guarded by the CLA-319 drift gate). We
   * keep the property in the schema so wire clients don't break; a follow-up
   * issue moves cron-job provisioning onto the gateway store path.
   *
   * @deprecated Not rendered into `openclaw.json`; cron jobs live in the
   * gateway store.
   */
  cron: Schema.optional(Schema.Array(ProfileCronJob)),
  gateway: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  browser: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  /**
   * Top-level `tools` slice of `openclaw.json`, passed through verbatim
   * into the rendered config patch (last-defined-wins across layers). This
   * is the capability-baseline seam — e.g. the neutral base layer declares
   * `tools.web.search` so every fresh box boots with Codex web search.
   *
   * Distinct from `bootstrap.tools` (the TOOLS.md workspace document): this
   * field is machine config the gateway reads, whereas `bootstrap.tools` is
   * human-facing documentation. Composed and emitted independently. A
   * connector bundle's `tools.exec.pathPrepend` fragment deep-merges into
   * whatever a layer declares here, so both coexist.
   *
   * @since 1.0.0
   */
  tools: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  /**
   * Top-level `memory` slice of `openclaw.json`, passed through verbatim
   * into the rendered config patch (last-defined-wins across layers). The
   * base layer no longer sets it: OpenClaw 2.0 (2026.8.1) removed the QMD
   * backend, so builtin memory is the only engine and the embedder lives
   * in `memorySearch` below. On a 2.0+ core the renderer merges
   * `memorySearch` into this slice as `memory.search`.
   *
   * Distinct from the memory PLUGINS declared in `plugins` (`memory-core`
   * / `active-memory`), which are `openclaw plugins install` specs; this
   * field is the machine config the gateway reads.
   *
   * @since 1.0.0
   */
  memory: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  /**
   * Embeddings/recall config, passed through verbatim (last-defined-wins
   * across layers). The neutral base layer sets the platform-shared OpenAI
   * embedder (`text-embedding-3-small` over `https://api.openai.com/v1`,
   * apiKey `${OPENAI_API_KEY}` resolved from gateway env) so memory recall
   * works on every box.
   *
   * WHERE it lands depends on the box's core (`usesLegacyConfigShape` in
   * `@fm/clawinstall/openclaw-version`): `memory.search` on OpenClaw 2.0+
   * (2026.8.1+), `agents.defaults.memorySearch` below it. Each core rejects
   * the other location.
   *
   * @since 1.0.0
   */
  memorySearch: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  /**
   * Skill Workshop policy lowered into `skills.workshop` — emitted ONLY on
   * OpenClaw 2.0+ cores (the key does not exist below 2026.8.1 and is
   * rejected there). The base layer sets `approvalPolicy: "pending"` +
   * `autonomous.mode: "propose"` so an agent proposes skill changes for a
   * human instead of rewriting its own skills (the 2.0 default is `auto`).
   *
   * @since 1.0.0
   */
  workshop: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.Unknown })),
  heartbeat: Schema.optional(ProfileHeartbeat),
  bootstrap: Schema.optional(ProfileBootstrap),
  credentials: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.String })),
  /**
   * Override for the rendered exec-secrets provider config. When omitted,
   * the renderer falls back to the Phase-2 sops default. Last-defined-wins
   * across layers.
   */
  secretsProvider: Schema.optional(ExecSecretsProvider),
  /**
   * OpenClaw plugins this layer declares. The renderer composes
   * last-wins by `spec` across layers and emits the composed list on
   * `RenderedProfile.plugins`; the agent shells out to
   * `openclaw plugins install <spec>` per entry after sops blob writes.
   */
  plugins: Schema.optional(Schema.Array(ProfilePlugin)),
  /**
   * Auth profile rows the renderer materializes for OpenClaw's
   * `auth-profiles.json`. Today only codex-bridge rows land here, but
   * the field is generic so future OAuth-token providers can join.
   * Composed last-wins by `profileId` across layers.
   *
   * @since 1.0.0
   */
  authProfiles: Schema.optional(Schema.Array(AuthProfile)),
  /**
   * Non-interactive CLI bootstrap steps the agent runs idempotently
   * during the apply step (M3 google-auth automation et al.). The
   * renderer composes layers last-wins by `runOnceKey` and additionally
   * derives one entry per google `installation_connections` row to
   * drive `gog auth import`.
   *
   * @since 1.0.0
   */
  cliBootstrap: Schema.optional(Schema.Array(ProfileCliBootstrap)),
  /**
   * Declarative on-disk credential files the M1 agent writes verbatim to
   * the installation. This is the M1 primitive every higher-level
   * credential emitter lowers into — auth-profiles, sops blobs, gogcli
   * credentials.json, etc. all collapse into entries on this array so the
   * agent has exactly one write path to drive.
   *
   * Empty (or omitted) on existing publish rows; rendered/agent code
   * tolerates an absent array as "no files to write".
   *
   * @since 1.0.0
   */
  credentialFiles: Schema.optional(Schema.Array(ProfileCredentialFile)),
});

/**
 * One file in a rendered {@link ConnectorPlanStep}. The renderer has
 * already substituted the non-secret tokens (`{{clientId}}`,
 * `{{clientSecret}}`, `{{accountKey}}`, `{{accountEmail}}`); the agent
 * substitutes the per-connection secret tokens
 * ({@link ACCESS_TOKEN_PLACEHOLDER} / {@link REFRESH_TOKEN_PLACEHOLDER})
 * before writing.
 *
 * @since 1.0.0
 */
export const ConnectorPlanFile = Schema.Struct({
  path: Schema.String,
  content: Schema.String,
  mode: Schema.String,
});
export type ConnectorPlanFile = Schema.Schema.Type<typeof ConnectorPlanFile>;

/**
 * One post-install command in a rendered {@link ConnectorPlanStep}.
 * Same substitution contract as {@link ConnectorPlanFile.content}.
 *
 * `stdin`, when present, is piped to the command's standard input — for
 * connectors whose CLI reads secrets/config from stdin rather than argv.
 *
 * @since 1.0.0
 */
export const ConnectorPlanCommand = Schema.Struct({
  argv: Schema.Array(Schema.String),
  env: Schema.optional(Schema.Record({ key: Schema.String, value: Schema.String })),
  stdin: Schema.optional(Schema.String),
});
export type ConnectorPlanCommand = Schema.Schema.Type<typeof ConnectorPlanCommand>;

/**
 * Single connector-pipeline step the renderer emits — one per enabled
 * `installation_connections` row whose provider's `ConnectorBundle`
 * declares plan fields. The agent's generic executor:
 *
 *   1. Runs `openclaw skills install <skillSlug>` (if `skillSlug` is set).
 *   2. Writes each {@link ConnectorPlanFile} atomically (mkdir + tmpfile
 *      + chmod + rename) after substituting the secret tokens.
 *   3. Runs each {@link ConnectorPlanCommand} in order via the agent's
 *      ShellRunner after substituting the secret tokens.
 *
 * Per-step success/failure is reported back via the migration-event audit
 * surface so the dashboard can surface a half-completed pipeline without
 * blocking the apply.
 *
 * @since 1.0.0
 */
export const ConnectorPlanStep = Schema.Struct({
  provider: Schema.String,
  accountKey: Schema.String,
  connectionId: Schema.optional(Schema.String),
  skillSlug: Schema.optional(Schema.String),
  files: Schema.Array(ConnectorPlanFile),
  commands: Schema.Array(ConnectorPlanCommand),
});
export type ConnectorPlanStep = Schema.Schema.Type<typeof ConnectorPlanStep>;
export type ProfileLayerData = Schema.Schema.Type<typeof ProfileLayerData>;
